import {
  AppStore,
  AuthStore,
  DataStore,
  ErrorStore,
  FormFieldName,
  FormFields,
  FormStore as FormStoreInterface,
} from 'interfaces';
import {
  CheckCardNumberRequest,
  CheckCardNumberResponse,
  ConfirmLookupRequest,
  ConfirmResponse,
  CreateP2PRequest,
  CreateP2PResponse,
  FastPayData,
  FieldProps,
  TransferData,
  VerifyRequest,
  VerifyResponse,
  AppleDataResponse,
  AppleDataRequest, GooglePaymentData
} from "types";
import {
  getBooleanValue,
  getError,
  getNumberValue,
  getStringValue,
  hasNoError,
  isNumberValue,
  toFixedNumber,
  validate
} from "utils";

import { API } from 'services';
import { Currency } from 'enums';
import { ENDPOINTS } from 'endpoints';
import { TEXT } from 'texts';
import { makeAutoObservable } from "mobx";
import { ClientJS } from 'clientjs';

const defaultAmountLimits = {
  min: 100,
  max: 2999900,
};

class FormStore implements FormStoreInterface {
  constructor(authStore: AuthStore, appStore: AppStore, dataStore: DataStore, errorStore: ErrorStore) {
    makeAutoObservable(this);
    this.authStore = authStore;
    this.appStore = appStore;
    this.dataStore = dataStore;
    this.errorStore = errorStore;
  }

  authStore: AuthStore;
  appStore: AppStore;
  dataStore: DataStore;
  errorStore: ErrorStore;

  timer: NodeJS.Timeout | null = null;
  isLocked: boolean = false;

  applePayMerchantId: string | null = null;
  googlePaymentData: GooglePaymentData | null = null;

  fastPayData: FastPayData | null = null;
  isPaymentCardVisible: boolean = false;

  fields: FormFields = {
    cardNumber: {
      rules: [validate.required(), validate.exactLength(16, TEXT.validationCardNotValid), validate.cardNumber()],
      error: null,
    },
    expireDate: {
      rules: [
        validate.required(),
        validate.exactLength(4, TEXT.validationExpireDateNotValid),
        validate.expireDate(TEXT.validationExpireDateNotValid),
      ],
      error: null,
    },
    cvv: {
      rules: [validate.required(), validate.exactLength(3, TEXT.validationCvvNotValid)],
      error: null,
    },
    amount: {
      rules: [
        validate.required(),
        validate.min(defaultAmountLimits.min, TEXT.minAmount),
        validate.max(defaultAmountLimits.max, TEXT.maxAmount),
      ],
      min: defaultAmountLimits.min,
      max: defaultAmountLimits.max,
      error: null,
    },
    city: {
      rules: [validate.required()],
      error: null,
    },
    address: {
      rules: [validate.required()],
      error: null,
    },
    name: {
      rules: [validate.required()],
      error: null,
    },
    commission: {
      isLoading: false,
    },
    otp: {
      error: null,
      rules: [validate.required()],
    },
    senderName: {
      error: null,
    },
  };

  setFields = (props: FieldProps<FormFieldName>[]) => {
    props.forEach(({ name, field, value }: FieldProps<FormFieldName>) => {
      switch (field) {
        case 'value':
          this.fields[name].value = value;
          this.fields[name].error && this.validate([name]);
          if (name === 'amount') {
            this.amount.validate();
          }
          if (name === 'amount' &&
            (!!this.cardNumber.value || this.applePayMerchantId || this.googlePaymentData?.googlePayMerchantId)
            && !this.cardNumber.error && this.amount.isValid()) {
            if (!this.isLocked) {
              if (this.timer) {
                clearTimeout(this.timer);
              }
              this.timer = setTimeout(() => {
                this.updateCommision();
              }, 800);
            }
          }
          if (name === 'cardNumber' && typeof value === "string" && (value as string).length === 16) {
            this.validate([name]);
            if (this.cardNumber.isValid()) {
              this.setFields([{ name: 'cardNumber', field: 'isLoading', value: true }]);
              this.checkCardNumber()
                .then((response) => {
                  this.dataStore.setTransferData(response);
                  this.setPaymentRules(response);
                  this.amount.validate();
                  if (
                    !this.dataStore.transferData.hasExchanged &&
                    this.dataStore.transferData.needExtraData &&
                    this.dataStore.transferData.exchangeRate
                  ) {
                    const roundedValue =
                      Math.round(this.amount.value / this.dataStore.transferData.exchangeRate / 100) * 100;

                    const amount = roundedValue > 100 ? roundedValue : 100;

                    if (this.amount.value !== 0) {
                      this.amount.setValue(amount);
                    }
                    this.dataStore.setTransferData({ hasExchanged: true });
                  }

                  if (
                    this.dataStore.transferData.hasExchanged &&
                    !this.dataStore.transferData.needExtraData &&
                    this.dataStore.transferData.exchangeRate
                  ) {
                    const roundedValue =
                      toFixedNumber((this.amount.value * this.dataStore.transferData.exchangeRate) / 100) * 100;

                    this.amount.setValue(roundedValue);
                    this.dataStore.setTransferData({ hasExchanged: false });
                  }

                  if (response.needExtraData && this.dataStore.currency !== Currency.EUR) {
                    this.dataStore.setCurrency(Currency.EUR);
                  }

                  if (!response.needExtraData && this.dataStore.currency !== Currency.UAH) {
                    this.dataStore.setCurrency(Currency.UAH);
                  }

                  if (this.amount.isValid()) {
                    this.updateCommision().catch(() => {
                      this.errorStore.setError({ title: TEXT.error, callback: this.updateCommision });
                    });
                  }
                })
                .catch(() => {
                  this.errorStore.setError({ title: TEXT.error, callback: this.checkCardNumber });
                })
                .finally(() => this.setFields([{ name: 'cardNumber', field: 'isLoading', value: false }]));
            }
          }
          if (name === 'cardNumber'
            && (this.applePayMerchantId || this.googlePaymentData?.googlePayMerchantId)
            && typeof value === "string" && (value as string).length === 0
          ) {
            this.setFields([{ name: 'cardNumber', field: 'isLoading', value: true }]);
            this.checkCardNumber()
              .then((response) => {
                this.dataStore.setTransferData(response);
                this.setPaymentRules(response);
                this.amount.validate();

                if (this.amount.isValid()) {
                  this.updateCommision().catch(() => {
                    this.errorStore.setError({ title: TEXT.error, callback: this.updateCommision });
                  });
                }
              })
              .catch(() => {
                this.errorStore.setError({ title: TEXT.error, callback: this.checkCardNumber });
              })
              .finally(() => this.setFields([{ name: 'cardNumber', field: 'isLoading', value: false }]));
          }
          break;
        case 'error':
          this.fields[name].error = value as string | null;
          break;
        case 'isLoading':
          this.fields[name].isLoading = value as boolean;
          break;
        case 'min':
          this.fields[name].min = value as number;
          break;
        case 'max':
          this.fields[name].max = value as number;
          break;
        case 'rules':
          this.fields[name].rules = value as Array<(value: string) => string | null>;
          break;
        default:
          break;
      }
    });
  };

  setFastPayData = (payData: FastPayData) => {
    this.fastPayData = payData
  };

  validate = (names: FormFieldName[]) => {
    const props: FieldProps<FormFieldName>[] = [];
    names.forEach((name: FormFieldName) => {
      props.push({
        name,
        field: 'error',
        value: validate.compose(this.fields[name].rules)(this.fields[name].value),
      });
    });
    this.setFields(props);
  };

  clearCardValues = () => {
    this.setFields([
      { name: 'cardNumber', field: 'value', value: null},
      { name: 'cardNumber', field: 'error', value: null},
      { name: 'expireDate', field: 'value', value: null},
      { name: 'expireDate', field: 'error', value: null},
      { name: 'cvv', field: 'value', value: null},
      { name: 'cvv', field: 'error', value: null},
    ]);
  };

  setPaymentRules = (data: TransferData) => {
    this.setFields([
      { name: 'amount', field: 'min', value: data.min },
      { name: 'amount', field: 'max', value: data.max },
      {
        name: 'amount',
        field: 'rules',
        value: [
          validate.required(),
          validate.min(data.min || defaultAmountLimits.min, TEXT.minAmount),
          validate.max(data.max || defaultAmountLimits.max, TEXT.maxAmount),
        ],
      },
    ]);
  }

  clearFastPayValues = () => {
    this.fastPayData = null
  };

  togglePaymentCardVisible = () => {
    this.isPaymentCardVisible = !this.isPaymentCardVisible;
  }

  submit = async (): Promise<CreateP2PResponse> => {
    const response = await API.post<CreateP2PRequest, CreateP2PResponse, unknown>({
      resource: ENDPOINTS.CREATE,
      options: {
        body: {
          sourceCard: {
            ...(this.cardNumber.value && this.expireDate.value && this.cvv.value && {
              cardNumber: this.cardNumber.value,
              expirationDate: this.expireDate.value,
              cvv: this.cvv.value,
            }),
            senderName: this.senderName.value,
            ...(this.dataStore.selectedParticipant?.participantId && {
              participantId: this.dataStore.selectedParticipant?.participantId
            }),
            ...((this.googlePaymentData || this.applePayMerchantId) && this.fastPayData)
          },
          targetCard: {
            splitId: this.appStore.id,
          },
          amount: this.amount.value,
          ...(this.dataStore.transferData.needExtraData && {
            extraRequisitesForSource: {
              city: this.city.value,
              address: this.address.value,
              name: this.name.value,
            },
          }),
          deviceInfo: this.clientDeviceInfo
        },
        headers: { Authorization: `Bearer ${this.authStore.token}`, 'Accept-Language': this.appStore.lang },
      },
    });
    return {
      orderStatus: response.data.orderStatus,
      orderId: response.data.orderId,
      confirmationType: response.data.confirmationType,
      confirmation2DS: response.data.confirmation2DS,
      confirmation3DS: response.data.confirmation3DS,
      confirmation3DS2: response.data.confirmation3DS2,
      confirmationOTP: response.data.confirmationOTP,
    };

    //TODO - refactor to to mock api

    // const response = require('../../mocks/PaymentCreate3Ds2MethodURLResponse.json');
    // return {
    //   orderStatus: response.payload.orderStatus,
    //   orderId: response.payload.orderId,
    //   confirmationType: response.payload.confirmationType,
    //   confirmation2DS: response.payload.confirmation2DS,
    //   confirmation3DS: response.payload.confirmation3DS,
    //   confirmation3DS2: response.payload.confirmation3DS2,
    //   confirmationOTP: response.payload.confirmationOTP,
    // };
  };

  confirm = async (): Promise<ConfirmResponse> => {
    const response = await API.post<ConfirmLookupRequest, ConfirmResponse, unknown>({
      resource: ENDPOINTS.CONFIRM,
      options: {
        body: {
          orderId: this.appStore.orderId,
          otp: this.otp.value,
          splitId: this.appStore.id,
        },
        headers: { Authorization: `Bearer ${this.authStore.token}`, 'Accept-Language': this.appStore.lang },
      },
    });
    return response.data;

    //TODO - refactor to to mock api

    // const response = require('../../mocks/PaymentConfirm3DsResponse.json');
    // return response.payload
  };

  checkCardNumber = async (): Promise<TransferData> => {
    const response = await API.post<CheckCardNumberRequest, CheckCardNumberResponse, unknown>({
      resource: ENDPOINTS.CHECK_CARD_NUMBER,
      options: {
        body: {
          splitId: this.appStore.id,
          ...(this.cardNumber.value ? { sourceCard: { cardNumber: this.cardNumber.value } } : (this.dataStore.isiOS ? { sourceCard: { applePay: true } } : { sourceCard: { googlePay: true } }))
        },
        headers: { Authorization: `Bearer ${this.authStore.token}`, 'Accept-Language': this.appStore.lang },
      },
    });

    const exchangeRate =
      Array.isArray(response.data.rates) && response.data.rates.find((rate) => rate.currency === 'EUR')?.buyRate;

    return {
      needExtraData: response.data.needExtraRequisitesForSource,
      title: response.data.transferTitle,
      description: response.data.transferDescription,
      commission: response.data.titleCommission,
      minCommission: response.data.titleMinComission,
      country: response.data.titleCountry,
      min: response.data.minAmount,
      max: response.data.maxAmount,
      ...(!!exchangeRate && { exchangeRate: toFixedNumber(Number(exchangeRate), 2) }),
      ...(response.data.applePayMerchantId && { applePayMerchantId: response.data.applePayMerchantId }),
      ...(response.data.googlePayMerchantId && { googlePaymentData: {
          googlePayMerchantId: response.data.googlePayMerchantId,
          googlePayGatewayMerchantId: response.data.googlePayGatewayMerchantId
        } as GooglePaymentData
      })
    };

    //
    // //TODO - refactor to to mock api
    // const response = require('../../mocks/PaymentRulesCheckResponseGoogle.json');
    // const exchangeRate =
    //   //@ts-ignore
    //   Array.isArray(response.payload.rates) && response.payload.rates.find((rate) => rate.currency === 'EUR')?.buyRate;
    //
    // return Promise.resolve({
    //   needExtraData: response.payload.needExtraRequisitesForSource,
    //   title: response.payload.transferTitle,
    //   description: response.payload.transferDescription,
    //   commission: response.payload.titleCommission,
    //   minCommission: response.payload.titleMinComission,
    //   country: response.payload.titleCountry,
    //   min: response.payload.minAmount,
    //   max: response.payload.maxAmount,
    //   ...(!!exchangeRate && { exchangeRate: toFixedNumber(Number(exchangeRate), 2) }),
    //   ...(response.payload.applePayMerchantId && {applePayMerchantId: response.payload.applePayMerchantId}),
    //   ...(response.payload.googlePayMerchantId && { googlePaymentData: {
    //       googlePayMerchantId: response.payload.googlePayMerchantId,
    //       googlePayGatewayMerchantId: response.payload.googlePayGatewayMerchantId
    //     } as GooglePaymentData
    //   })
    // })
  };

  setIsLocked = (bool: boolean) => {
    this.isLocked = bool;
  };

  updateCommision = async () => {
    this.setFields([{ name: 'commission', field: 'isLoading', value: true }]);
    const response = await API.post<VerifyRequest, VerifyResponse, unknown>({
      resource: ENDPOINTS.GET_COMMISSION,
      options: {
        body: {
          amount: this.amount.value,
          splitId: this.appStore.id,
          ...(this.cardNumber.value ? { sourceCard: { cardNumber: this.cardNumber.value } } : (this.dataStore.isiOS ? { sourceCard: { applePay: true } } : { sourceCard: { googlePay: true } }))
        },
        headers: { Authorization: `Bearer ${this.authStore.token}`, 'Accept-Language': this.appStore.lang },
      },
    });

    //TODO - refactor to to mock api

    // const response = require('../../mocks/PaymentVerifyResponse.json');

    this.setIsLocked(true);
    this.setFields([
      {
        name: 'commission',
        field: 'value',
        value: response.data.commission,
      },
      {
        name: 'commission',
        field: 'isLoading',
        value: false,
      },
    ]);
    this.setIsLocked(false);
    if (this.timer) {
      clearTimeout(this.timer);
    }
  };

  validateMerchant = async (validationURL: string): Promise<AppleDataResponse> => {
    const response = await API.post<AppleDataRequest, AppleDataResponse, unknown>({
      resource: ENDPOINTS.VALIDATE_MERCHANT,
      options: {
        body: {
          validationURL,
        },
        headers: { Authorization: `Bearer ${this.authStore.token}`, 'Accept-Language': this.appStore.lang },
      },
    });

    return {
      opaqueApplePaySessionObject: response.data.opaqueApplePaySessionObject,
      success: response.data.success
    };
  }

  setGooglePaymentData = (paymentData: GooglePaymentData) => {
    this.googlePaymentData = paymentData;
  };

  setApplePayMerchantId = (merchantId: string) => {
    this.applePayMerchantId = merchantId;
  };

  get cardNumber() {
    return {
      isLoading: getBooleanValue(this.fields.cardNumber.isLoading),
      value: getStringValue(this.fields.cardNumber.value),
      error: getError(this.fields.cardNumber.error),
      isValid: () => !!this.fields.cardNumber.value && hasNoError({ params: [this.fields.cardNumber.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'cardNumber', field: 'value', value }]),
      validate: () => this.validate(['cardNumber']),
    };
  }
  get expireDate() {
    return {
      value: getStringValue(this.fields.expireDate.value),
      error: getError(this.fields.expireDate.error),
      isValid: () => !!this.fields.expireDate.value && hasNoError({ params: [this.fields.expireDate.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'expireDate', field: 'value', value }]),
      validate: () => this.validate(['expireDate']),
    };
  }

  get cvv() {
    return {
      value: getStringValue(this.fields.cvv.value),
      error: getError(this.fields.cvv.error),
      isValid: () => !!this.fields.cvv.value && hasNoError({ params: [this.fields.cvv.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'cvv', field: 'value', value }]),
      validate: () => this.validate(['cvv']),
    };
  }

  get amount() {
    return {
      value: getNumberValue(this.fields.amount.value),
      error: getError(this.fields.amount.error),
      min: this.fields.amount.min,
      max: this.fields.amount.max,
      isValid: () => isNumberValue(this.fields.amount.value) && hasNoError({ params: [this.fields.amount.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'amount', field: 'value', value }]),
      validate: () => this.validate(['amount']),
    };
  }

  get city() {
    return {
      value: getStringValue(this.fields.city.value),
      error: getError(this.fields.city.error),
      setValue: (value: unknown) => this.setFields([{ name: 'city', field: 'value', value }]),
      validate: () => this.validate(['city']),
    };
  }

  get address() {
    return {
      value: getStringValue(this.fields.address.value),
      error: getError(this.fields.address.error),
      setValue: (value: unknown) => this.setFields([{ name: 'address', field: 'value', value }]),
      validate: () => this.validate(['address']),
    };
  }

  get name() {
    return {
      value: getStringValue(this.fields.name.value),
      error: getError(this.fields.name.error),
      setValue: (value: unknown) => this.setFields([{ name: 'name', field: 'value', value }]),
      validate: () => this.validate(['name']),
    };
  }

  get commission() {
    return {
      isLoading: getBooleanValue(this.fields.commission.isLoading),
      value: getNumberValue(this.fields.commission.value),
      error: getError(this.fields.commission.error),
      isValid: () =>
        isNumberValue(this.fields.commission.value) && hasNoError({ params: [this.fields.commission.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'commission', field: 'value', value }]),
      validate: () => this.validate(['commission']),
    };
  }

  get otp() {
    return {
      value: getStringValue(this.fields.otp.value),
      error: getError(this.fields.otp.error),
      isValid: () => !!this.fields.otp.value && hasNoError({ params: [this.fields.otp.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'otp', field: 'value', value }]),
      validate: () => this.validate(['otp']),
    };
  }

  get senderName() {
    return {
      value: getStringValue(this.fields.senderName.value),
      error: getError(this.fields.senderName.error),
      isValid: () => hasNoError({ params: [this.fields.senderName.error] }),
      setValue: (value: unknown) => this.setFields([{ name: 'senderName', field: 'value', value }]),
      validate: () => this.validate(['senderName']),
    };
  }

  get isP2PValid() {
    return this.cardNumber.isValid() && this.cvv.isValid() && this.expireDate.isValid() && this.amount.isValid();
  }

  get isLookupValid() {
    return this.otp.isValid();
  }

  get isTransferValid() {
    return ![this.city.error, this.address.error, this.name.error].some((value?: string | null) => !!value);
  }

  get isDataLoading() {
    return this.cardNumber.isLoading || this.commission.isLoading;
  }

  get clientDeviceInfo() {
    const client = new ClientJS();
    const screen = client.getCurrentResolution().split("x");

    return {
      userAgent: client.getUserAgent(),
      os: client.getOS(),
      osVersion: client.getOSVersion(),
      device: client.getDevice(),
      deviceType: client.getDeviceType(),
      isMobile: client.isMobile(),
      screenPrint: client.getScreenPrint(),
      colorDepth: client.getColorDepth(),
      screenHeight: screen && screen[1] ? screen[1] : 0,
      screenWidth: screen && screen[0] ? screen[0] : 0,
      plugins: client.getPlugins(),
      isJavaEnabled: client.isJava(),
      browserLanguage: client.getLanguage(),
      browserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      browserTimeZoneOffset: new Date().getTimezoneOffset()
    };
  }
}

export default FormStore;
